import React from "react";
import { observer } from "mobx-react-lite";
import { CPLN_HELM_RELEASE_LEGACY_PREFIX, request, resourceLink } from "../../services/cpln";
import { ConsoleContext } from "../../mobxStores/consoleContext/consoleContext";
import { Route, useParams } from "react-router-dom";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { DetailLayout } from "../../components/detail/layout";
import { DetailContext } from "../../components/detail/detailContext";
import { Loader } from "../../components/layout/loader";
import { HelmReleaseInstance, LegacyReleaseState } from "../marketplace/types/release";
import { fetchLegacyReleaseItems } from "../marketplace/utils";
import NGAlert from "../../newcomponents/alert";
import { LegacyReleaseRevisions } from "./legacyRevisions";
import { LegacyReleaseInfo } from "./legacyInfo";

type RouteParams = "release";
const HelmLegacyReleaseDetailRaw: React.FC = () => {
  const { release: releaseName } = useParams<RouteParams>();

  const basePathUpdated = useBasePath(`/helmrelease/legacy/${releaseName}/*`);

  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [release, setRelease] = React.useState<HelmReleaseInstance>(null as any);

  const [items, setItems] = React.useState<any[]>([]);
  const [itemsNotFound, setItemsNotFound] = React.useState<any[]>([]);
  const [state, setState] = React.useState<LegacyReleaseState>({ deployments: [], release: "", schemaVersion: "" });

  React.useEffect(() => {
    fetchRelease();
  }, []);

  React.useEffect(() => {
    if (!release) {
      return;
    }
    if (state.deployments.length < 1) {
      return;
    }

    fetchItems();
  }, [state]);

  React.useEffect(() => {
    if (!release) {
      return;
    }

    // TODO Get agent health
    // TODO Get volumeset info
  }, [items]);

  async function fetchRelease() {
    try {
      setIsLoading(true);
      const { data } = await request({
        url: resourceLink("secret", CPLN_HELM_RELEASE_LEGACY_PREFIX + releaseName) + "/-reveal",
      });

      const _state = JSON.parse(data.data.payload);
      setState(_state);

      const revision = _state.deployments.toSorted((a, b) => {
        if (a.revision > b.revision) return -1;
        if (b.revision > a.revision) return 1;
        return 0;
      })[0].revision;

      setRelease({
        kind: "helm-release",
        lastModified: data.lastModified,
        created: data.created,
        tags: data.tags,
        gvc: "",
        org: ConsoleContext.org!,
        name: releaseName!,
        id: releaseName!,
        template: "",
        version: "",
        revision: revision,
      });
      setIsLoading(false);
    } catch (e) {
      let errorMessage = e.response?.data?.error;
      if (!errorMessage) errorMessage = e.message;
      setError(errorMessage);
      setIsLoading(false);
    }
  }

  async function fetchItems() {
    try {
      const revision = state.deployments.toSorted((a, b) => {
        if (a.revision > b.revision) return -1;
        if (b.revision > a.revision) return 1;
        return 0;
      })[0];
      const _itemResults = await fetchLegacyReleaseItems(revision.resources);
      const _itemsNotFound = _itemResults.filter((_i) => _i.notFound === true);
      const _items = _itemResults.filter((_i) => !_i.notFound);

      const kindOrder = [
        "gvc",
        "workload",
        "volumeset",
        "identity",
        "domain",
        "agent",
        "secret",
        "cloudaccount",
        "group",
        "serviceaccount",
        "policy",
        "auditctx",
      ];

      let _sortedItems: any[] = [];
      for (const kind of kindOrder) {
        _sortedItems = _sortedItems.concat(..._items.filter((i) => i.kind === kind));
      }
      setItems(_sortedItems);
      let _sortedItemsNotFound: any[] = [];
      for (const kind of kindOrder) {
        _sortedItemsNotFound = _sortedItemsNotFound.concat(..._itemsNotFound.filter((i) => i.kind === kind));
      }
      setItemsNotFound(_sortedItemsNotFound);
      setIsLoading(false);
    } catch (e) {
      let errorMessage = e.response?.data?.error;
      if (!errorMessage) errorMessage = e.message;
      setError(errorMessage);
      setIsLoading(false);
    }
  }

  if (error) {
    return (
      <>
        <div>Release</div>
        <pre>{JSON.stringify(release, null, 2)}</pre>
        <div>State</div>
        <pre>{JSON.stringify(state, null, 2)}</pre>
        <NGAlert type={"error"} title="Error" message={`${error}`} />;
      </>
    );
  }

  if (!release || isLoading) {
    return <Loader reason={"Fetching data"} />;
  }

  return (
    <>
      <BasePathContext.Provider value={basePathUpdated}>
        <DetailContext.Provider value={{ item: release, fetchItem: fetchRelease }}>
          <DetailLayout
            links={[
              { label: "Info", url: "-info" },
              { label: "Revisions", url: "-revisions" },
            ]}
          >
            <Route
              path={`-info`}
              element={<LegacyReleaseInfo release={release} items={items} itemsNotFound={itemsNotFound} />}
            />
            <Route path={`-revisions`} element={<LegacyReleaseRevisions release={release} state={state} />} />
          </DetailLayout>
        </DetailContext.Provider>
      </BasePathContext.Provider>
    </>
  );
};

export const HelmLegacyReleaseDetail = observer(HelmLegacyReleaseDetailRaw);
